import React from 'react';
import { useTranslation } from 'react-i18next';
import { unescapeHTML, titleize } from 'underscore.string';
import { NavLink } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@cargomatic/ui';
import { LifeBuoy, LogOut, User } from 'lucide-react';
import CargomaticLogo from 'img/cargomatic-logo-dark.png';
import SessionStore from 'stores/SessionStore';
import classNames from 'classnames';
import Navbar from 'components/v2/navs/Navbar';
import UserActionCreators from 'actions/UserActionCreators';
import { Avatar } from 'components/modules/user/Avatar';
import LanguageSelector from 'components/site/language-selector';

const SUPPORT_MAILTO = 'mailto:support@cargomatic.com';

const dropdownItemClass =
  'gap-2 text-gray-900 no-underline hover:no-underline focus:bg-gray-100 focus:text-gray-900 focus:outline-none focus-visible:outline-none';

function logout() {
  UserActionCreators.logout();
}

export default function WarehouseNavigation() {
  const { t } = useTranslation();

  function renderMobileNav(onClose) {
    return [
      <NavLink
        className="nav-item"
        to="/warehouse/user/profile"
        onClick={onClose}
        key="mobile-profile"
      >
        <span>{titleize(t('PROFILE', { count: 1 }))}</span>
      </NavLink>,
      <a
        href={SUPPORT_MAILTO}
        className="nav-item"
        onClick={onClose}
        key="mobile-contact-support"
      >
        <span>{t('CONTACT_SUPPORT')}</span>
      </a>,
      <a href="#" className="nav-item" onClick={logout} key="mobile-signout">
        {t('SIGN_OUT')}
      </a>,
    ];
  }
  function renderRightNav() {
    const name = SessionStore.getUser().name;
    const userId = SessionStore.getUser()._id;

    const profileButton = (
      <button
        type="button"
        data-testid="profile-button"
        title={unescapeHTML(name)}
        className="flex-shrink-0 flex h-8 w-8 items-center justify-center text-sm bg-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
        aria-haspopup="true"
        aria-label={unescapeHTML(name)}
      >
        <Avatar
          key={userId}
          userId={userId}
          altName={unescapeHTML(name)}
          className="w-8 h-8 rounded-full object-cover"
        />
      </button>
    );

    return [
      <div
        key="language-selector"
        data-testid="warhouse-nav-language-selector"
        className={classNames(
          'flex items-center justify-center nav-item mr-2',
          { hidden: LANGUAGE_SELECTION_DISABLED },
        )}
      >
        <LanguageSelector />
      </div>,
      <DropdownMenu key="main-dropdown">
        <DropdownMenuTrigger asChild>{profileButton}</DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem asChild className={dropdownItemClass}>
            <NavLink to="/warehouse/user/profile">
              <User className="h-4 w-4" />
              <span>{titleize(t('PROFILE', { count: 1 }))}</span>
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className={dropdownItemClass}>
            <a href={SUPPORT_MAILTO}>
              <LifeBuoy className="h-4 w-4" />
              <span>{t('CONTACT_SUPPORT')}</span>
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem className={dropdownItemClass} onClick={logout}>
            <LogOut className="h-4 w-4" />
            <span>{t('SIGN_OUT')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>,
    ];
  }
  const brandLogo = (
    <NavLink to="/" key="brand-logo">
      <img alt="Cargomatic" src={CargomaticLogo} style={{ height: 25 }} />
    </NavLink>
  );

  return (
    <Navbar
      logo={brandLogo}
      renderRightNav={renderRightNav}
      renderMobileNav={renderMobileNav}
    />
  );
}
